import { useEffect, useState, useCallback } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Play, Square, RefreshCw, Sparkles, ExternalLink, Globe, MapPin, Building2 } from "lucide-react";

const SCOPE_OPTIONS = [
  { key: "us_states", label: "50 US States" },
  { key: "india_states", label: "28 Indian States" },
  { key: "countries", label: "~195 Countries" },
  { key: "cities", label: "Cities" },
];

const MODES = [
  { key: "skip", label: "Skip populated", help: "Only regenerate empty/stub articles (safest)" },
  { key: "merge", label: "Merge (fill gaps)", help: "Keep existing content, only fill missing sections" },
  { key: "overwrite", label: "Overwrite everything", help: "Replace all content with fresh AI generation" },
];

function KindIcon({ kind, className = "w-4 h-4" }) {
  if (kind === "country") return <Globe className={className} />;
  if (kind === "state") return <MapPin className={className} />;
  return <Building2 className={className} />;
}

export default function AdminLocations() {
  const [scope, setScope] = useState({ us_states: true, india_states: true, countries: true, cities: false });
  const [mode, setMode] = useState("merge");
  const [status, setStatus] = useState(null);
  const [list, setList] = useState([]);
  const [filterKind, setFilterKind] = useState("");
  const [q, setQ] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [regenerating, setRegenerating] = useState({});

  const fetchStatus = useCallback(async () => {
    try {
      const r = await http.get("/admin/locations/generate-status");
      setStatus(r.data);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchList = useCallback(async () => {
    setLoadingList(true);
    try {
      const params = new URLSearchParams();
      if (filterKind) params.set("kind", filterKind);
      if (q) params.set("q", q);
      params.set("limit", "500");
      const r = await http.get(`/admin/locations/list?${params.toString()}`);
      setList(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load locations");
    } finally {
      setLoadingList(false);
    }
  }, [filterKind, q]);

  useEffect(() => { fetchStatus(); fetchList(); }, [fetchStatus, fetchList]);

  // Poll status every 5s while a batch is running; every 15s otherwise so
  // the auto-scheduler card stays fresh even when the admin isn't clicking.
  useEffect(() => {
    const isRunning = status?.state === "running";
    const iv = setInterval(() => { fetchStatus(); if (isRunning) fetchList(); }, isRunning ? 5000 : 15000);
    return () => clearInterval(iv);
  }, [status?.state, fetchStatus, fetchList]);

  const start = async () => {
    try {
      const body = { ...scope, mode };
      const r = await http.post("/admin/locations/generate-batch", body);
      toast.success(`Batch started — ${r.data?.scope ? "queued" : ""}`);
      setTimeout(() => { fetchStatus(); fetchList(); }, 500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start batch");
    }
  };

  const cancel = async () => {
    try {
      await http.post("/admin/locations/generate-cancel");
      toast.success("Cancel signal sent");
      setTimeout(fetchStatus, 500);
    } catch (e) {
      toast.error("Failed to cancel");
    }
  };

  const seedCountries = async () => {
    try {
      const r = await http.post("/admin/locations/seed-countries");
      toast.success(`Countries seeded: +${r.data?.inserted} inserted, ${r.data?.skipped} skipped`);
      fetchList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    }
  };

  const regen = async (slug) => {
    setRegenerating((s) => ({ ...s, [slug]: true }));
    try {
      await http.post(`/admin/locations/${slug}/regenerate`, { mode: "overwrite" });
      toast.success(`Regeneration queued for ${slug}`);
      // Poll list every 5s until it finishes
      const iv = setInterval(async () => {
        await fetchList();
        setList((cur) => {
          const row = cur.find((r) => r.slug === slug);
          if (row && !row.ai_generating) {
            clearInterval(iv);
            setRegenerating((s) => { const n = { ...s }; delete n[slug]; return n; });
          }
          return cur;
        });
      }, 6000);
      // Safety unlock after 15 min
      setTimeout(() => {
        clearInterval(iv);
        setRegenerating((s) => { const n = { ...s }; delete n[slug]; return n; });
      }, 15 * 60 * 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regen failed");
      setRegenerating((s) => { const n = { ...s }; delete n[slug]; return n; });
    }
  };

  const anyScope = Object.values(scope).some(Boolean);
  const running = status?.state === "running";
  const pct = status?.total ? Math.round(((status.done || 0) / status.total) * 100) : 0;

  return (
    <div data-testid="admin-locations-panel" className="space-y-8">
      {/* Instructions */}
      <div className="rounded-2xl border p-6" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-card, #fff)" }}>
        <h3 className="font-display text-2xl flex items-center gap-2">
          <Sparkles className="w-5 h-5" style={{ color: "#7C3AED" }} />
          Location AI Encyclopedia — Batch Generator
        </h3>
        <p className="text-sm mt-2" style={{ color: "var(--wofy-text-soft)" }}>
          Fills the 48-section encyclopedia profile + 10–25 curated outbound links for
          every location you select. Rotates across Claude Sonnet 4.5 → GPT-5.4 → Gemini
          3.1 Pro whenever a provider is slow or rate-limited. Progress persists after each
          chunk, so users see content appear even mid-generation.
        </p>
        <ol className="text-xs mt-3 space-y-1 list-decimal list-inside" style={{ color: "var(--wofy-text-soft)" }}>
          <li>
            First time only — click <span className="font-semibold">Seed 195 countries</span> to
            populate country stubs.
          </li>
          <li>Choose scopes + mode below, then hit <span className="font-semibold">Start batch</span>.</li>
          <li>Each location generates in 3 chunks; ~2–5 min per location depending on the LLM.</li>
          <li>Users visiting any un-generated location page will also auto-trigger generation on view.</li>
        </ol>
      </div>

      {/* Controls */}
      <div className="rounded-2xl border p-6 space-y-5" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-card, #fff)" }}>
        <div>
          <div className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: "var(--wofy-text-soft)" }}>Scope</div>
          <div className="flex flex-wrap gap-2">
            {SCOPE_OPTIONS.map((s) => (
              <button
                key={s.key}
                data-testid={`scope-${s.key}`}
                onClick={() => setScope((v) => ({ ...v, [s.key]: !v[s.key] }))}
                className="px-3 py-2 rounded-full text-sm border transition-colors"
                style={scope[s.key]
                  ? { background: "#2563EB", color: "#fff", borderColor: "#2563EB" }
                  : { background: "transparent", color: "var(--wofy-text)", borderColor: "var(--wofy-border)" }
                }
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: "var(--wofy-text-soft)" }}>Mode</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {MODES.map((m) => (
              <button
                key={m.key}
                data-testid={`mode-${m.key}`}
                onClick={() => setMode(m.key)}
                className="text-left px-3 py-3 rounded-lg border transition-colors"
                style={mode === m.key
                  ? { background: "#EEF2FF", color: "#4338CA", borderColor: "#4338CA" }
                  : { background: "transparent", color: "var(--wofy-text)", borderColor: "var(--wofy-border)" }
                }
              >
                <div className="text-sm font-semibold">{m.label}</div>
                <div className="text-xs mt-0.5 opacity-70">{m.help}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {!running ? (
            <button
              data-testid="start-batch"
              onClick={start}
              disabled={!anyScope}
              className="wofy-btn-primary inline-flex disabled:opacity-40"
            >
              <Play className="w-4 h-4" /> Start batch
            </button>
          ) : (
            <button data-testid="cancel-batch" onClick={cancel} className="wofy-btn-secondary inline-flex">
              <Square className="w-4 h-4" /> Cancel batch
            </button>
          )}
          <button data-testid="seed-countries" onClick={seedCountries} className="wofy-btn-secondary inline-flex">
            <Globe className="w-4 h-4" /> Seed 195 countries
          </button>
          <button data-testid="refresh-status" onClick={() => { fetchStatus(); fetchList(); }} className="wofy-btn-secondary inline-flex">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Auto-scheduler status */}
      {status?.auto && (
        <div className="rounded-2xl border p-5 flex flex-wrap items-center gap-4" style={{ borderColor: "var(--wofy-border)", background: "linear-gradient(135deg, #F0FDF4 0%, #EEF2FF 100%)" }} data-testid="auto-status-card">
          <div className="flex items-center gap-2">
            <span className={"w-2 h-2 rounded-full " + (status.auto.enabled && status.auto.running ? "animate-pulse" : "")} style={{ background: status.auto.enabled && status.auto.running ? "#16A34A" : "#9CA3AF" }} />
            <span className="text-xs font-mono uppercase tracking-widest font-semibold" style={{ color: "#166534" }}>
              Auto-scheduler {status.auto.enabled ? (status.auto.running ? "RUNNING" : "starting…") : "DISABLED"}
            </span>
          </div>
          <div className="text-xs opacity-80">
            {status.auto.current_slug ? (
              <>Now filling <span className="font-mono font-semibold">{status.auto.current_slug}</span></>
            ) : status.auto.last_result === "all_filled" ? (
              <>All locations already filled — sleeping until next tick.</>
            ) : status.auto.last_result === "llm_budget_cooldown" ? (
              <>LLM budget cooling down — will resume automatically.</>
            ) : status.auto.last_result === "yielded_to_admin_batch" ? (
              <>Paused while admin batch is running.</>
            ) : (
              <>Idle · last result: <span className="font-mono">{status.auto.last_result || "—"}</span></>
            )}
          </div>
          <div className="ml-auto text-xs opacity-70">
            <span className="mr-3">cycles <b>{status.auto.cycles}</b></span>
            <span className="mr-3 text-green-700">ok <b>{status.auto.ok}</b></span>
            <span className="text-red-700">failed <b>{status.auto.failed}</b></span>
          </div>
        </div>
      )}

      {/* Live status */}
      {status && status.state !== "idle" && (
        <div className="rounded-2xl border p-6" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-card, #fff)" }} data-testid="batch-status-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              {running ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#2563EB" }} /> : null}
              <span className="text-sm font-semibold uppercase tracking-widest" style={{ color: running ? "#2563EB" : "var(--wofy-text-soft)" }}>
                {status.state}
              </span>
              <span className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
                {status.mode} · started {status.started_at ? new Date(status.started_at).toLocaleTimeString() : "—"}
              </span>
            </div>
            <div className="text-sm">
              <span className="font-semibold">{status.done}</span> / {status.total}
              <span className="text-green-600 ml-3">✓ {status.ok}</span>
              <span className="text-amber-600 ml-3">⚠ {status.skipped}</span>
              <span className="text-red-600 ml-3">✗ {status.failed}</span>
            </div>
          </div>
          <div className="mt-3 h-2 rounded-full overflow-hidden" style={{ background: "var(--wofy-border)" }}>
            <div className="h-full transition-all" style={{ width: `${pct}%`, background: "linear-gradient(90deg, #2563EB, #7C3AED)" }} />
          </div>
          {(status.recent || []).length > 0 && (
            <div className="mt-4 max-h-64 overflow-y-auto text-xs font-mono">
              {status.recent.map((r, i) => (
                <div key={i} className="py-1 flex items-center gap-2" style={{ color: r.ok ? "var(--wofy-text)" : "#B91C1C" }}>
                  <span>{r.ok ? "✓" : "✗"}</span>
                  <span className="opacity-60">{r.at ? new Date(r.at).toLocaleTimeString() : ""}</span>
                  <span className="font-semibold">{r.slug}</span>
                  <span className="opacity-70">{r.reason}</span>
                  {r.filled_after != null && <span className="opacity-60">({r.filled_after}/48)</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Locations list */}
      <div className="rounded-2xl border p-6" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-card, #fff)" }}>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h3 className="font-display text-xl">All locations ({list.length})</h3>
          <div className="flex items-center gap-2">
            <select
              value={filterKind}
              onChange={(e) => setFilterKind(e.target.value)}
              className="px-3 py-2 text-sm rounded-md border"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="filter-kind"
            >
              <option value="">All kinds</option>
              <option value="country">Countries</option>
              <option value="state">States</option>
              <option value="city">Cities</option>
            </select>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name…"
              className="px-3 py-2 text-sm rounded-md border"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="filter-search"
            />
            <button onClick={fetchList} className="wofy-btn-secondary inline-flex text-sm" data-testid="apply-filters">
              <RefreshCw className="w-3.5 h-3.5" /> Apply
            </button>
          </div>
        </div>
        {loadingList ? (
          <div className="py-14 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="locations-table">
              <thead>
                <tr className="text-left uppercase text-xs" style={{ color: "var(--wofy-text-soft)" }}>
                  <th className="py-2 pr-3">Kind</th>
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Country / State</th>
                  <th className="py-2 pr-3 text-right">Sections</th>
                  <th className="py-2 pr-3">Last generated</th>
                  <th className="py-2 pr-3">Article</th>
                  <th className="py-2 pr-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((row) => {
                  const isGen = !!row.ai_generating || !!regenerating[row.slug];
                  const isRich = (row.filled || 0) >= 5;
                  const trail = [row.country, row.state, row.city].filter(Boolean).join(" · ");
                  return (
                    <tr key={row.slug} className="border-t" style={{ borderColor: "var(--wofy-border)" }} data-testid={`loc-row-${row.slug}`}>
                      <td className="py-2 pr-3">
                        <span className="inline-flex items-center gap-1 text-xs font-mono uppercase" style={{ color: "var(--wofy-text-soft)" }}>
                          <KindIcon kind={row.kind} className="w-3.5 h-3.5" />{row.kind}
                        </span>
                      </td>
                      <td className="py-2 pr-3 font-semibold">{row.name}</td>
                      <td className="py-2 pr-3 text-xs opacity-80">{trail || "—"}</td>
                      <td className="py-2 pr-3 text-right">
                        <span className="font-mono" style={{ color: isRich ? "#059669" : "#B91C1C" }}>
                          {row.filled || 0}/48
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-xs opacity-70">
                        {row.ai_generated_at ? new Date(row.ai_generated_at).toLocaleString() : "—"}
                      </td>
                      <td className="py-2 pr-3">
                        <a href={`/l/${row.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs" style={{ color: "#2563EB" }}>
                          Open <ExternalLink className="w-3 h-3" />
                        </a>
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <a
                          href={`/admin/location/${row.slug}`}
                          className="text-xs px-2 py-1 rounded border inline-flex items-center gap-1 mr-1"
                          style={{ borderColor: "var(--wofy-border)" }}
                          data-testid={`edit-${row.slug}`}
                        >
                          Edit
                        </a>
                        <button
                          onClick={() => regen(row.slug)}
                          disabled={isGen || running}
                          className="text-xs px-2 py-1 rounded border disabled:opacity-40 inline-flex items-center gap-1"
                          style={{ borderColor: "var(--wofy-border)" }}
                          data-testid={`regen-${row.slug}`}
                        >
                          {isGen ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                          {isGen ? "Generating…" : "Regenerate"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {list.length === 0 && (
                  <tr><td colSpan="7" className="py-10 text-center" style={{ color: "var(--wofy-text-soft)" }}>No locations yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
